import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Check, Copy } from 'lucide-react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

const hoverOptions = { delay: 2000 };
const sharedIconProps = {
  size: 14,
};

interface CopyTextProps {
  id: string;
  value?: string;
}

const CopyText: FC<CopyTextProps> = ({ id, value }) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const [hasCopied, setHasCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dataTestId = `copy-btn-${id}`;

  const handleCopy = () => {
    if (value) {
      copyToClipboard(value);
      setHasCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setHasCopied(false), 2000);
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Stack alignItems="center" gap={4} justifyContent="start" orientation="horizontal">
      {value ?? ''}
      {!!value && (
        <Tooltip
          content={hasCopied ? 'Copied!' : 'Click here to copy.'}
          hoverOptions={hoverOptions}
          placement="right-start"
          trigger={
            <Button
              aria-label={hasCopied ? 'Copied!' : 'Copy to clipboard'}
              data-testid={dataTestId}
              onPress={handleCopy}
              size="sm"
              visual={hasCopied ? 'success' : 'primary'}
            >
              {hasCopied ? <Check {...sharedIconProps} /> : <Copy {...sharedIconProps} />}
            </Button>
          }
        />
      )}
    </Stack>
  );
};

export default CopyText;
